/* WK UNFOLD - nothing on the site changes in a single frame (2026-09-03).

   The owner's rule: wherever something appears, disappears or changes size,
   it travels there. This file is the one owner of that motion for the three
   shapes it takes outside the product page's own JS:

     1. <details>/<summary> folds (Specifications, Care, the rail's Full
        description). The box's height travels, the copy fades, and the lines
        of text land one after another - the unfurl. The element still owns
        its state: the click is intercepted only long enough to run the
        animation; `open` is set before measuring on the way in and cleared
        after finishing on the way out. Every <details> on a page is wired
        automatically except the header's mega-menu (Impulse animates that one
        itself) and anything inside Apippa's calculator.

     2. Swaps (wkUnfold.morph): a block whose contents change in one go - the
        care guide's material tabs, the trade wizard's steps. The caller keeps
        its own hidden-toggling code and hands it over as a function; this
        measures the block before and after, runs the height between the two
        while whatever came out of hiding fades up into place. Exits are
        instant on purpose - the same doctrine as the calculator (custom.css
        section 46): two motions fighting over one box is the jank, one honest
        layout with the height travelling is not.

     3. The Shop grid reloading on a filter or sort. Impulse dims the old grid
        while it fetches and then drops the new one in at full strength; the
        new grid now fades up from the same dimness.

   Interrupted mid-travel, a fold continues from wherever it is rather than
   jumping to an end first. Settlement always runs through two routes - the
   animation's promise and a timer - because neither is dependable alone: the
   promise can stay unsettled in a tab that is not rendering, and a timer alone
   would fight a cancel. Reduced motion gets the native, instant behaviour,
   untouched.

   Loaded deferred on every page from layout/theme.liquid, before
   wk-product-page.js (which hands over the rail's disclosure explicitly). */
(function () {
  'use strict';
  if (window.wkUnfold) return;

  var EASE = 'cubic-bezier(.25, .1, .25, 1)';        /* height travel */
  var EASE_OUT = 'cubic-bezier(.22, .61, .36, 1)';   /* entries settling (--wk-ease-underline) */

  function still() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }
  function canAnimate(el) {
    return !!(el && el.animate) && typeof Animation !== 'undefined' && 'finished' in Animation.prototype && !still();
  }

  /* Travel time from the distance: a two-line fold should not crawl and a
     long panel should not snap. 240ms at nothing, 440ms from ~480px up. */
  function travel(px) { return Math.round(Math.min(440, Math.max(240, 200 + px * 0.5))); }

  /* `clip` rather than `hidden`: hidden makes the box a scroll container,
     which un-sticks anything position:sticky inside it for the length of the
     animation (the trade wizard's nav bar would have jumped). Older engines
     without clip get hidden. */
  function clip(el) {
    el.style.overflow = 'clip';
    if (getComputedStyle(el).overflow !== 'clip') el.style.overflow = 'hidden';
  }

  /* Whatever came into view fades up, one after another. fill:backwards holds
     the start state through the delay; after the end the effect is dropped and
     the element is simply itself again - nothing is left written on it. */
  function fadeIn(els, base, step) {
    els.forEach(function (el, i) {
      if (!el.animate) return;
      el.animate(
        [{ opacity: 0, transform: 'translateY(5px)' }, { opacity: 1, transform: 'none' }],
        { duration: 300, delay: base + Math.min(i, 8) * step, easing: EASE_OUT, fill: 'backwards' });
    });
  }

  /* The lines of a fold's copy: its block children, with lists opened up so
     each item is a line of its own. */
  function lines(body) {
    var out = [];
    [].forEach.call(body.children, function (c) {
      if (c.tagName === 'UL' || c.tagName === 'OL') [].push.apply(out, c.children);
      else out.push(c);
    });
    return out;
  }

  /* Settlement through two routes, whichever arrives first. A run that was
     superseded (cancelled by a newer click on the same element) is marked
     __wkOff and never settles - the newer run owns the element now. */
  function whenDone(anim, ms, fn) {
    var done = false;
    function settle() {
      if (done || anim.__wkOff) return;
      done = true;
      /* Reached through the timer the animation may still be mid-flight and
         still applying a height, so it has to be let go of or the box keeps
         whatever height it was frozen at. Through the promise it is finished
         and this does nothing. */
      if (anim.playState !== 'finished') { try { anim.cancel(); } catch (e) {} }
      fn();
    }
    anim.finished.then(settle, function () { /* cancelled: leave it alone */ });
    window.setTimeout(settle, ms + 60);
  }

  /* The fold's body: the one element beside the summary, or a wrapper put
     around whatever else is there so there is one box to size. */
  function resolveBody(d, summary) {
    var nodes = [].filter.call(d.childNodes, function (n) {
      return n !== summary && !(n.nodeType === 3 && !n.nodeValue.trim());
    });
    if (!nodes.length) return null;
    if (nodes.length === 1 && nodes[0].nodeType === 1) return nodes[0];
    var wrap = document.createElement('div');
    wrap.className = 'wk-unfold__body';
    nodes.forEach(function (n) { wrap.appendChild(n); });
    d.appendChild(wrap);
    return wrap;
  }

  function details(d, body) {
    if (!d || d.tagName !== 'DETAILS' || d.hasAttribute('data-wk-unfold')) return;
    var summary = null;
    for (var i = 0; i < d.children.length; i++) {
      if (d.children[i].tagName === 'SUMMARY') { summary = d.children[i]; break; }
    }
    if (!summary) return;
    body = body || resolveBody(d, summary);
    if (!body) return;
    /* The state is exposed for CSS: opening / open / closing / closed. The
       chevrons turn on `closing`, not on `open` coming off at the very end. */
    d.setAttribute('data-wk-unfold', d.open ? 'open' : 'closed');
    var running = null;

    summary.addEventListener('click', function (ev) {
      if (!canAnimate(body)) return;            /* native, instant */
      ev.preventDefault();

      /* The direction comes from the state, not from `open`: a close in flight
         still has `open` on (it comes off at settlement), and a click during
         it means "open again". */
      var state = d.getAttribute('data-wk-unfold');
      var opening = state === 'closing' ? true : state === 'opening' ? false : !d.open;

      /* Interrupted mid-travel: carry on from where the box is - its height,
         its padding and its opacity as the cancelled run left them. */
      var mid = null;
      if (running) {
        var cs = getComputedStyle(body);
        mid = { h: body.getBoundingClientRect().height, pt: cs.paddingTop, pb: cs.paddingBottom, o: parseFloat(cs.opacity) || 0 };
        running.__wkOff = true;
        running.cancel();
        running = null;
      }

      if (opening) d.open = true;               /* has to be open to have a height */
      d.setAttribute('data-wk-unfold', opening ? 'opening' : 'closing');
      /* Border-box, so the copy's padding is inside the travelling height - and
         the padding travels too, because a border-box can never be shorter
         than its own padding: left alone, a close would stop 18px short and
         then snap. */
      body.style.boxSizing = 'border-box';
      clip(body);

      var full = body.getBoundingClientRect().height;
      var rest = getComputedStyle(body);
      var pt = rest.paddingTop, pb = rest.paddingBottom;
      var start = mid || (opening ? { h: 0, pt: '0px', pb: '0px', o: 0 } : { h: full, pt: pt, pb: pb, o: 1 });
      var end = opening ? { h: full, pt: pt, pb: pb, o: 1 } : { h: 0, pt: '0px', pb: '0px', o: 0 };
      var D = travel(Math.abs(end.h - start.h));

      var a = body.animate(
        [{ height: start.h + 'px', paddingTop: start.pt, paddingBottom: start.pb, opacity: start.o },
         { height: end.h + 'px', paddingTop: end.pt, paddingBottom: end.pb, opacity: end.o }],
        { duration: D, easing: EASE });
      running = a;
      if (opening && !mid) fadeIn(lines(body), 70, 38);

      whenDone(a, D, function () {
        body.style.overflow = '';
        body.style.boxSizing = '';
        if (!opening) d.open = false;
        d.setAttribute('data-wk-unfold', opening ? 'open' : 'closed');
        running = null;
      });
    });
  }

  /* A block whose contents are swapped in one go. `mutate` is the caller's own
     toggling; this runs it between two measurements. */
  function morph(container, mutate) {
    if (typeof mutate !== 'function') return;
    if (!container || !canAnimate(container)) { mutate(); return; }

    var wasHidden = [].slice.call(container.querySelectorAll('[hidden]'));
    var h0 = container.getBoundingClientRect().height;   /* mid-travel this is the travelling height */
    if (container.__wkMorph) {
      container.__wkMorph.__wkOff = true;
      container.__wkMorph.cancel();
      container.__wkMorph = null;
    }
    mutate();
    var h1 = container.getBoundingClientRect().height;

    fadeIn(wasHidden.filter(function (el) { return !el.hidden && el.getClientRects().length; }), 40, 30);

    if (Math.abs(h1 - h0) < 1) return;
    container.style.boxSizing = 'border-box';
    clip(container);
    var D = travel(Math.abs(h1 - h0));
    var a = container.animate([{ height: h0 + 'px' }, { height: h1 + 'px' }], { duration: D, easing: EASE });
    container.__wkMorph = a;
    whenDone(a, D, function () {
      container.style.overflow = '';
      container.style.boxSizing = '';
      container.__wkMorph = null;
    });
  }

  function wire(scope) {
    [].forEach.call((scope || document).querySelectorAll('details'), function (d) {
      if (d.hasAttribute('data-wk-unfold')) return;              /* wired, or opted out with data-wk-unfold="off" */
      if (d.classList.contains('site-nav__details')) return;     /* Impulse's mega-menu animates itself */
      if (d.closest('#calculator')) return;                      /* the calculator's own folds belong to wk-product-page.js */
      details(d);
    });
  }

  /* The Shop grid on a filter or sort: Impulse's AjaxRenderer replaces the
     children of #CollectionAjaxContent, and the dimmed old grid (.unload,
     opacity .2) is swapped for a fresh one at full strength. Meet it at the
     same dimness and bring it up. childList only, no subtree: one callback per
     reload. */
  function watchGrid() {
    var ajax = document.getElementById('CollectionAjaxContent');
    if (!ajax || !window.MutationObserver) return;
    new MutationObserver(function () {
      if (still()) return;
      var w = ajax.querySelector('.collection-grid__wrapper') || ajax;
      if (w.animate) w.animate([{ opacity: 0.2 }, { opacity: 1 }], { duration: 320, easing: 'ease-out' });
    }).observe(ajax, { childList: true });
  }

  window.wkUnfold = { details: details, morph: morph, wire: wire };

  wire();
  watchGrid();
  document.addEventListener('shopify:section:load', function (ev) { wire(ev.target); });
})();
